import type { LogLevel, LogReportRequest } from 'shared-models/browser';
import { authenticatedInstance } from '../index/index.service';
import { getDeviceId } from '../../utils/device-id.util';

const DEDUP_WINDOW_MS = 60_000;
const MAX_REPORTS_PER_SESSION = 20;

// Benign browser quirks that are not real exceptions — never persisted to the logs sink.
const IGNORED_MESSAGE_PATTERNS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
];

const recentSignatures = new Map<string, number>();
let reportCount = 0;

type ReportInput = {
  message: string;
  errorName?: string;
  trace?: string;
  level?: LogLevel;
  context?: Record<string, unknown>;
};

// Fire-and-forget browser→backend crash reporter; posts to the public /logs sink and never throws.
export function reportError(input: ReportInput): void {
  try {
    if (process.env.NODE_ENV === 'test') {
      return;
    }
    if (reportCount >= MAX_REPORTS_PER_SESSION) {
      return;
    }
    if (IGNORED_MESSAGE_PATTERNS.some((pattern) => input.message.includes(pattern))) {
      return;
    }
    const route = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.hash}` : undefined;
    const signature = `${input.errorName ?? ''}|${input.message}|${route ?? ''}`;
    const now = Date.now();
    const last = recentSignatures.get(signature);
    if (last != null && now - last < DEDUP_WINDOW_MS) {
      return;
    }
    recentSignatures.set(signature, now);
    reportCount += 1;

    const context = readBestEffortContext();
    const payload: LogReportRequest = {
      level: input.level ?? 'error',
      message: String(input.message).slice(0, 2000),
      errorName: input.errorName,
      trace: input.trace ? input.trace.slice(0, 8000) : undefined,
      route,
      deviceId: getDeviceId(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      context: sanitize(input.context),
      ...context,
    };
    const token = process.env.REACT_APP_CLIENT_LOG_TOKEN;
    void authenticatedInstance
      .post('/logs', payload, { headers: token ? { 'x-client-log-token': token } : undefined })
      .catch(() => undefined);
  } catch {
    // Diagnostics must never break the calling flow.
  }
}

// Builds a report from a window 'error' event; prefers real stack, enriches masked rows.
export function buildErrorEventReport(event: ErrorEvent): ReportInput {
  const error = event.error instanceof Error ? event.error : undefined;
  const stack = error?.stack;
  const isMasked = !stack && (!event.message || event.message === 'Script error.');
  return {
    message: event.message || error?.message || 'Unhandled error',
    errorName: error?.name ?? 'Error',
    trace: stack,
    context: isMasked ? buildMaskedErrorContext(event) : undefined,
  };
}

// Salvages what the browser exposes for an opaque 'Script error.' (no stack): coords + page state.
function buildMaskedErrorContext(event: ErrorEvent): Record<string, unknown> {
  const context: Record<string, unknown> = { masked: true };
  if (event.filename) {
    context.filename = event.filename;
  }
  if (Number.isFinite(event.lineno)) {
    context.lineno = event.lineno;
  }
  if (Number.isFinite(event.colno)) {
    context.colno = event.colno;
  }
  if (typeof document !== 'undefined') {
    context.visibilityState = document.visibilityState;
  }
  if (typeof window !== 'undefined') {
    context.lastRoute = `${window.location.pathname}${window.location.hash}`;
  }
  return context;
}

let globalHandlersInstalled = false;

// Installs window error + unhandledrejection reporters once at app boot (real crashes only).
export function installGlobalErrorReporting(): void {
  if (globalHandlersInstalled || typeof window === 'undefined') {
    return;
  }
  globalHandlersInstalled = true;
  window.addEventListener('error', (event) => {
    reportError(buildErrorEventReport(event));
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError({
      message: reason instanceof Error ? reason.message : String(reason),
      errorName: reason instanceof Error ? reason.name : 'UnhandledRejection',
      trace: reason instanceof Error ? reason.stack : undefined,
    });
  });
}

// Best-effort tenant/employee/session pulled from localStorage at call time; all fields optional.
function readBestEffortContext(): Pick<LogReportRequest, 'tenantId' | 'employeeName' | 'sessionLabel'> {
  try {
    const tenantId = localStorage.getItem('selectedTenantId') ?? undefined;
    if (!tenantId) {
      return {};
    }
    const raw = localStorage.getItem(`posEmployeeSession:${tenantId}`);
    if (!raw) {
      return { tenantId };
    }
    const parsed = JSON.parse(raw) as { employee?: { fullName?: string }; activeSessionId?: string };
    return {
      tenantId,
      employeeName: parsed.employee?.fullName,
      sessionLabel: parsed.activeSessionId,
    };
  } catch {
    return {};
  }
}

function sanitize(data: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (data === undefined) {
    return undefined;
  }
  try {
    return JSON.parse(JSON.stringify(data)) as Record<string, unknown>;
  } catch {
    return { unserializable: String(data) };
  }
}
